import {
  ConnectedPosition,
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  OverlayModule,
} from "@angular/cdk/overlay";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ViewChild,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { RouterOutlet } from "@angular/router";
import { FuseLoadingBarComponent } from "@fuse/components/loading-bar";
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from "@fuse/components/navigation";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { Navigation } from "app/core/navigation/navigation.types";
import { PageHeaderComponent } from "app/layout/common/page-header/page-header.component";
import { UserComponent } from "app/layout/common/user/user.component";
import { Subject, fromEvent, takeUntil } from "rxjs";
import { MarqueeComponent } from "../../../../shared/components/marquee/marquee.component";
import { MatTooltip } from "@angular/material/tooltip";

@Component({
  selector: "dense-layout",
  templateUrl: "./dense.component.html",
  styleUrl: "./dense.component.scss",
  encapsulation: ViewEncapsulation.None,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    OverlayModule,
    MatButtonModule,
    MatIconModule,
    UserComponent,
    RouterOutlet,
    MarqueeComponent,
    PageHeaderComponent,
    MatTooltip,
  ],
})
export class DenseLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly SIDEBAR_LOGO_HINT_STORAGE_KEY =
    "aem:dense-sidebar-logo-hint-dismissed";
  private static readonly SIDEBAR_EXPANDED_STORAGE_KEY =
    "aem:dense-sidebar-expanded";
  private static readonly SIDEBAR_TRANSITION_DURATION_MS = 450;

  @Input() navigation: Navigation;
  @ViewChild(CdkConnectedOverlay)
  private readonly _sidebarLogoHintOverlay?: CdkConnectedOverlay;
  @ViewChild(CdkOverlayOrigin)
  private readonly _sidebarLogoHintOrigin?: CdkOverlayOrigin;

  isScreenSmall: boolean;
  isNavigationExpanded: boolean = this.getSidebarExpanded();
  protected showSidebarLogoHint: boolean = false;
  protected isSidebarLogoHintOriginVisible: boolean = true;
  protected sidebarLogoState: "a" | "b" = "a";
  protected readonly sidebarLogoHintPositions: ConnectedPosition[] = [
    {
      originX: "end",
      originY: "center",
      overlayX: "start",
      overlayY: "center",
      offsetX: 12,
    },
    {
      originX: "start",
      originY: "center",
      overlayX: "end",
      overlayY: "center",
      offsetX: -12,
    },
  ];
  protected readonly sidebarLogoHintOverlayClass = "dense-logo-hint-overlay";

  navigationAppearance: "default" | "dense" = "dense";
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _navigationContentElement: HTMLElement | null = null;
  private _navigationResizeObserver: ResizeObserver | null = null;

  marqueeItems = [];

  /**
   * Constructor
   */
  constructor(
    private readonly _elementRef: ElementRef<HTMLElement>,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.showSidebarLogoHint = !this.getSidebarLogoHintDismissed();

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes("md");

        // Change the navigation appearance
        this.navigationAppearance = this.isScreenSmall ? "default" : "dense";
      });
  }

  ngAfterViewInit(): void {
    const navigationElement =
      this._elementRef.nativeElement.querySelector<HTMLElement>(
        "fuse-vertical-navigation",
      );
    const navigationContent =
      this._elementRef.nativeElement.querySelector<HTMLElement>(
        ".fuse-vertical-navigation-content",
      );

    if (!navigationElement || !navigationContent) {
      return;
    }

    this._navigationContentElement = navigationContent;
    this.refreshSidebarLogoHintPosition();

    fromEvent(navigationContent, "scroll")
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this.refreshSidebarLogoHintPosition());

    fromEvent(window, "resize")
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this.refreshSidebarLogoHintPosition());

    if (typeof ResizeObserver !== "undefined") {
      this._navigationResizeObserver = new ResizeObserver(() =>
        this.refreshSidebarLogoHintPosition(),
      );
      this._navigationResizeObserver.observe(navigationElement);
      this._navigationResizeObserver.observe(navigationContent);
    }

    this.scheduleSidebarLogoHintPositionUpdate();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this._navigationResizeObserver?.disconnect();
    this._navigationResizeObserver = null;

    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    if (!this.isScreenSmall) {
      this.toggleNavigationAppearance();
      return;
    }

    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name,
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  /**
   * Toggle the navigation appearance
   */
  toggleNavigationAppearance(): void {
    this.isNavigationExpanded = !this.isNavigationExpanded;
    this.navigationAppearance = "dense";
    this.sidebarLogoState = "a";
    this.saveSidebarExpanded(this.isNavigationExpanded);

    this._changeDetectorRef.detectChanges();
    this._fuseNavigationService
      .getComponent<FuseVerticalNavigationComponent>("mainNavigation")
      ?.refresh();
    this.scheduleSidebarLogoHintPositionUpdate();
  }

  /**
   * Handle clicks on the persistent brand logo button. While collapsed the
   * logo acts as the expand trigger; while expanded the dedicated collapse
   * button handles closing, so the logo is purely decorative.
   */
  protected onLogoClick(): void {
    if (!this.isScreenSmall && this.isNavigationExpanded) {
      return;
    }

    this.toggleNavigation("mainNavigation");
  }

  /**
   * Swap the logo for the sidebar toggle icon on hover, but only while the
   * logo is acting as the expand trigger (collapsed / small screen).
   */
  protected onLogoMouseEnter(): void {
    if (!this.isScreenSmall && this.isNavigationExpanded) {
      return;
    }

    this.sidebarLogoState = "b";
  }

  protected dismissSidebarLogoHint(): void {
    this.showSidebarLogoHint = false;

    try {
      localStorage.setItem(
        DenseLayoutComponent.SIDEBAR_LOGO_HINT_STORAGE_KEY,
        "true",
      );
    } catch {
      // Ignore storage failures and only dismiss for the current session.
    }
  }

  private getSidebarExpanded(): boolean {
    try {
      return (
        localStorage.getItem(
          DenseLayoutComponent.SIDEBAR_EXPANDED_STORAGE_KEY,
        ) === "true"
      );
    } catch {
      return false;
    }
  }

  private saveSidebarExpanded(expanded: boolean): void {
    try {
      localStorage.setItem(
        DenseLayoutComponent.SIDEBAR_EXPANDED_STORAGE_KEY,
        String(expanded),
      );
    } catch {
      // Ignore storage failures — state simply won't persist this session.
    }
  }

  private getSidebarLogoHintDismissed(): boolean {
    try {
      return (
        localStorage.getItem(
          DenseLayoutComponent.SIDEBAR_LOGO_HINT_STORAGE_KEY,
        ) === "true"
      );
    } catch {
      return false;
    }
  }

  private updateSidebarLogoHintVisibility(
    navigationContent: HTMLElement,
  ): void {
    const originElement =
      this._sidebarLogoHintOrigin?.elementRef.nativeElement ?? null;

    if (!originElement) {
      this.isSidebarLogoHintOriginVisible = false;
      return;
    }

    const originRect = originElement.getBoundingClientRect();
    const containerRect = navigationContent.getBoundingClientRect();

    const isVisible =
      originRect.bottom > containerRect.top &&
      originRect.top < containerRect.bottom &&
      originRect.right > containerRect.left &&
      originRect.left < containerRect.right;

    this.isSidebarLogoHintOriginVisible = isVisible;
    this._changeDetectorRef.markForCheck();
  }

  private refreshSidebarLogoHintPosition(): void {
    if (!this._navigationContentElement) {
      return;
    }

    this.updateSidebarLogoHintVisibility(this._navigationContentElement);

    if (
      !this.showSidebarLogoHint ||
      this.isScreenSmall ||
      this.isNavigationExpanded ||
      !this.isSidebarLogoHintOriginVisible
    ) {
      return;
    }

    this._sidebarLogoHintOverlay?.overlayRef?.updatePosition();
  }

  private scheduleSidebarLogoHintPositionUpdate(): void {
    const refreshPosition = () => this.refreshSidebarLogoHintPosition();

    requestAnimationFrame(() => {
      refreshPosition();

      requestAnimationFrame(() => refreshPosition());
    });

    setTimeout(
      () => refreshPosition(),
      DenseLayoutComponent.SIDEBAR_TRANSITION_DURATION_MS,
    );
  }
}
